/**
 * RealRoomingTab
 * ──────────────────────────────────────────────────────────────────────
 * Real-mode wrapper around `<RoomingByRole />` that fetches the rooming
 * surface from the AriaOne 2026-05-04 §M9k API and binds the kanban
 * actions back to the corresponding write endpoints.
 *
 * Endpoints consumed (all `/api/scheduler/rooming/*`):
 *   GET  /queue?location_id&date          → items[]
 *   GET  /rooms?location_id               → rooms[]
 *   GET  /providers?location_id           → providers[]
 *   POST /assign  body{ appointment_id, room_id }
 *   POST /release/{appointment_id}
 *
 * Other lifecycle transitions (arrived, waiting_room, scheduled,
 * with_provider) reuse the canonical
 *   PATCH /api/scheduler/appointments/{id}/status   (vendor §M9d)
 * via `schedulerApi.updateAppointmentStatus`.
 *
 * Demo-mode is NEVER reachable from this component — `EchoScheduler.js`
 * delegates to `LegacyEchoScheduler` for the demo path before this
 * tab can be mounted.
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { toast } from 'sonner';
import { Users, AlertCircle, RefreshCw } from 'lucide-react';
import * as schedulerApi from '../../../../lib/api/schedulerApi';
import RoomingByRole from './RoomingByRole';
import RealRoomingOrchestrator from './RealRoomingOrchestrator';
import { useReportSchedulerSource } from '../../../../lib/scheduler/SchedulerTraceContext';

const todayIsoString = () => new Date().toISOString().slice(0, 10);

/* Translate an API error into a single user-facing toast. The status
 * codes our backend emits map directly:
 *   409  → room already occupied (collision)
 *   422  → invalid transition / room-at-wrong-location / terminal-state
 *   403  → role lacks the required permission
 *   404  → appointment / location not found
 *   else → generic message
 */
const toastApiError = (err, fallback = 'Action failed.') => {
  if (!err) {
    toast.error(fallback);
    return;
  }
  const status = err.status;
  const msg = err.message || fallback;
  if (status === 409) {
    toast.error('Room is already occupied — pick another room.', {
      description: msg,
    });
  } else if (status === 422) {
    toast.error('Cannot apply this transition.', { description: msg });
  } else if (status === 403) {
    toast.error('Permission denied — your role cannot perform this action.', {
      description: msg,
    });
  } else if (status === 404) {
    toast.error('Appointment not found — it may have been cancelled.', {
      description: msg,
    });
  } else {
    toast.error(fallback, { description: msg });
  }
};

const RealRoomingTab = ({
  role,
  locationId,
  providerId = null,
  date,
}) => {
  const [items, setItems] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Tracks per-appointment in-flight ops so retries don't double-fire.
  const [pending, setPending] = useState(() => new Set());

  const targetDate = date || todayIsoString();

  const refetch = useCallback(async () => {
    if (!locationId) {
      setItems([]);
      setRooms([]);
      setProviders([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const [qRes, rRes, pRes] = await Promise.all([
      schedulerApi.getRoomingQueue({ location_id: locationId, date: targetDate }),
      schedulerApi.getRoomingRooms({ location_id: locationId }),
      schedulerApi.getRoomingProviders({ location_id: locationId }),
    ]);
    if (!qRes?.ok) {
      setError(qRes?.error || { message: 'Failed to load rooming queue.' });
      setItems([]);
    } else {
      setItems(Array.isArray(qRes.data?.items) ? qRes.data.items : []);
    }
    if (rRes?.ok) {
      setRooms(Array.isArray(rRes.data?.rooms) ? rRes.data.rooms : []);
    } else {
      setRooms([]);
    }
    if (pRes?.ok) {
      setProviders(Array.isArray(pRes.data?.providers) ? pRes.data.providers : []);
    } else {
      setProviders([]);
    }
    setLoading(false);
  }, [locationId, targetDate]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  /* Pin an in-flight op for an appointment so the kanban dropdown
   * doesn't dispatch the same write twice if the user clicks fast. */
  const guard = useCallback((id) => {
    if (!id) return false;
    if (pending.has(id)) return false;
    setPending((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    return true;
  }, [pending]);

  const release = useCallback((id) => {
    setPending((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  /* Optimistic UI update: replace one item by id (used after a write
   * succeeds). The server response is the source of truth — we patch
   * the local state from `body.appointment` instead of guessing. */
  const patchItemFromAppt = useCallback((appt) => {
    if (!appt?.id) return;
    setItems((prev) => {
      const idx = prev.findIndex((it) => it.id === appt.id);
      if (idx === -1) return prev;
      const merged = {
        ...prev[idx],
        status: appt.status || prev[idx].status,
        roomId: appt.room_id ?? null,
        roomName: appt.room_id && appt.room_id.includes('::R')
          ? `Room ${appt.room_id.split('::R').pop()}`
          : null,
      };
      const next = prev.slice();
      next[idx] = merged;
      return next;
    });
  }, []);

  const handleMoveTo = useCallback(async (item, toStatus) => {
    if (!item?.id || !toStatus) return;
    if (!guard(item.id)) return;
    try {
      if (toStatus === 'in_exam') {
        // Need a room — pick the first available one.
        const available = rooms.find((r) => r.status === 'available');
        if (!available) {
          toast.error('No available rooms at this location.');
          return;
        }
        const res = await schedulerApi.assignRoom({
          appointment_id: item.id,
          room_id: available.id,
        });
        if (!res?.ok) {
          toastApiError(res?.error, 'Could not assign room.');
          await refetch();
          return;
        }
        patchItemFromAppt(res.data?.appointment);
        toast.success(`${item.name || 'Patient'} placed in ${available.name}.`);
        // Refresh rooms/providers since occupancy changed elsewhere.
        await refetch();
      } else if (toStatus === 'ready_checkout') {
        const res = await schedulerApi.releaseRoom(item.id);
        if (!res?.ok) {
          toastApiError(res?.error, 'Could not release the room.');
          await refetch();
          return;
        }
        patchItemFromAppt(res.data?.appointment);
        toast.success(`${item.name || 'Patient'} ready for checkout.`);
        await refetch();
      } else {
        // Generic lifecycle transition through PATCH /status.
        const res = await schedulerApi.updateAppointmentStatus(item.id, {
          status: toStatus,
        });
        if (!res?.ok) {
          toastApiError(res?.error, 'Could not update status.');
          await refetch();
          return;
        }
        patchItemFromAppt(res.data?.appointment);
        toast.success(`${item.name || 'Patient'} → ${toStatus.replace('_', ' ')}.`);
      }
    } finally {
      release(item.id);
    }
  }, [guard, release, rooms, patchItemFromAppt, refetch]);

  const handleRoomPatient = useCallback(async (item /* , availableRooms */) => {
    // MA "Room patient" entry point — same backend path as in_exam.
    await handleMoveTo(item, 'in_exam');
  }, [handleMoveTo]);

  /* ──────────── render ──────────── */

  // Compute source state BEFORE any early returns so the hook is
  // called unconditionally every render (rules of hooks).
  const roomingSource =
    !locationId          ? 'no_location'
    : error              ? (error.status === 404 || error.status === 501 ? 'api_missing' : 'error')
    : loading            ? 'loading'
    : items.length > 0   ? 'live'
    : 'empty';
  useReportSchedulerSource('rooming', roomingSource);

  if (!locationId) {
    return (
      <Card className="border-dashed border-slate-200" data-testid="rooming-no-location" data-rooming-source="no_location">
        <CardContent className="p-4 text-center">
          <Users className="h-5 w-5 text-slate-300 mx-auto mb-1" />
          <p className="text-[11px] text-slate-500">
            Pick a location to load the rooming queue.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-rose-200 bg-rose-50/40" data-testid="rooming-error" data-rooming-source={roomingSource}>
        <CardContent className="p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-[11px] text-rose-700">
            <AlertCircle className="h-4 w-4" />
            <span>{error.message || 'Failed to load rooming.'}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={refetch}
            data-testid="rooming-retry-btn"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const isEmpty = !loading && items.length === 0;

  return (
    <div className="space-y-3" data-testid="rooming-real" data-rooming-source={roomingSource}>
      {/* AI swap-suggestions strip — full structural parity with Legacy
          `<RoomingOrchestrator/>` but pulls from real
          /api/scheduler/rooming/swaps. Renders structured empty state
          when the endpoint isn't provisioned yet. Applies/dismisses
          refresh the parent kanban via onActionDone so a swap that
          moves a patient between lanes shows up immediately. */}
      <RealRoomingOrchestrator
        locationId={locationId}
        date={date || todayIsoString()}
        allowApply={role !== 'read_only' && role !== 'provider'}
        onActionDone={refetch}
      />
      {loading && (
        <div className="text-[11px] text-slate-500" data-testid="rooming-loading">
          Loading rooming…
        </div>
      )}
      {isEmpty && (
        <Card className="border-dashed border-slate-200" data-testid="rooming-empty-real">
          <CardContent className="p-5 text-center">
            <Users className="h-5 w-5 text-slate-300 mx-auto mb-1.5" />
            <p className="text-[12px] font-medium text-slate-700">No active patients in flow</p>
            <p className="text-[10px] text-slate-500 mt-0.5">
              This location&apos;s rooming queue is empty for {date || 'today'}.
              Patients move into the queue automatically when they check in
              from the <strong>Today</strong> tab.
            </p>
            <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1 mt-2" onClick={refetch} data-testid="rooming-empty-refresh">
              <RefreshCw className="h-3 w-3" /> Refresh queue
            </Button>
          </CardContent>
        </Card>
      )}
      <RoomingByRole
        role={role}
        items={items}
        rooms={rooms}
        providers={providers}
        alerts={[]}
        providerId={providerId}
        onMoveTo={handleMoveTo}
        onRoomPatient={handleRoomPatient}
      />
    </div>
  );
};

export default RealRoomingTab;
